import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Branded heading block used by the document design system.
 *  - variant "chapter" -> <h2 class="doc-chapter"> (big section divider)
 *  - variant "section" -> <h3 class="doc-section"> (numbered section w/ accent tab)
 * The text is edited inline; the brand colour / accent come from the document
 * theme stylesheet at render time.
 */
export const BrandedHeading = Node.create({
  name: "brandedHeading",
  // Higher than StarterKit's heading so `h2.doc-chapter` / `h3.doc-section`
  // are claimed here (with their class) rather than parsed as plain headings.
  priority: 200,
  group: "block",
  content: "inline*",
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "section",
        parseHTML: (el) =>
          el.getAttribute("data-variant") ||
          (el.classList.contains("doc-chapter") ? "chapter" : "section"),
        renderHTML: (attrs) => ({ "data-variant": attrs.variant }),
      },
    };
  },

  parseHTML() {
    return [
      { tag: "h2.doc-chapter", attrs: { variant: "chapter" } },
      { tag: "h3.doc-section", attrs: { variant: "section" } },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const isChapter = node.attrs.variant === "chapter";
    return [
      isChapter ? "h2" : "h3",
      mergeAttributes(HTMLAttributes, {
        class: isChapter ? "doc-chapter" : "doc-section",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      insertBrandedHeading:
        (variant = "section") =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { variant },
            content: [
              {
                type: "text",
                text:
                  variant === "chapter"
                    ? "Chapter Title"
                    : "1. Section Title",
              },
            ],
          }),
    };
  },
});

export default BrandedHeading;
